/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from 'react'
import { toast } from 'sonner'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { useSuspendOwnerMutation } from '@/hooks/usePlatformSuspendOwner'
import AppDialog, { ModalActions } from '@/components/CustomModal'

const schema = z.object({
  reason: z.string().trim().min(2, 'Raison requise').max(256, 'Max 256'),
})
type Values = z.infer<typeof schema>

export function SuspendOwnerModal({
  ownerId,
  ownerName,
  disabled,
  onDone,
}: {
  ownerId: string
  ownerName?: string
  disabled?: boolean
  onDone?: () => void
}) {
  const [open, setOpen] = React.useState(false)
  const mut = useSuspendOwnerMutation(ownerId)

  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { reason: '' },
    mode: 'onChange',
  })

  const closeAndReset = () => {
    setOpen(false)
    setTimeout(() => form.reset({ reason: '' }), 0)
  }

  const submit = async (v: Values) => {
    try {
      await mut.mutateAsync({ reason: v.reason.trim() })
      toast.success('Owner suspendu', { description: ownerName ?? undefined })
      closeAndReset()
      onDone?.()
    } catch (e: any) {
      toast.error('Suspension impossible', {
        description: e?.response?.data?.message ?? 'Erreur inconnue',
      })
    }
  }

  return (
    <AppDialog
      open={open}
      onOpenChange={(v) => {
        if (!v) closeAndReset()
        else setOpen(true)
      }}
      title="Suspendre l'owner"
      description='Désactive l’accès à la boutique. À utiliser si impayé / fraude / etc.'
      trigger={
        <Button
          variant='destructive'
          size='sm'
          disabled={disabled}
          onClick={() => setOpen(true)}
        >
          Suspend
        </Button>
      }
      footer={
        <ModalActions
          cancelLabel='Annuler'
          confirmLabel='Suspendre'
          confirmVariant='destructive'
          confirmLoading={mut.isPending}
          confirmDisabled={!form.formState.isValid || mut.isPending}
          onConfirm={form.handleSubmit(submit)}
        />
      }
    >
      <Form {...form}>
        <div className='space-y-4'>
          <div className='rounded-lg border p-3 text-sm'>
            <div className='font-medium'>Owner</div>
            <div className='text-muted-foreground'>
              {ownerName ?? '—'} • <span className='font-mono'>{ownerId}</span>
            </div>
          </div>

          <FormField
            control={form.control}
            name='reason'
            render={({ field }) => (
              <FormItem>
                <FormLabel>Raison</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    autoFocus
                    placeholder='Ex: impayé février'
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      </Form>
    </AppDialog>
  )
}
